import { useMemo } from 'react';
import {
  FdrCoordinates,
  FdrImage,
  FdrLink,
  ProductInfoB2B,
} from '@hotelplan/supergraph-api';
import { IAdventureTravelRouteProduct } from 'components/domain/fdr-adventure-travel-routes';
import {
  isFdrAdventureTravelProduct,
  isFdrRoundTripProduct,
} from 'components/domain/fdr-product/fdr-product.utils';
import { fdrGeoMapProductVars } from 'components/page/geo/geo.vars';
import {
  FdrGeoMapProductsQueryVariables,
  useFdrGeoMapProductsQuery,
} from 'schemas/query/geo/fdr-geo-map-products.generated';
import { isFdrThGeoObject } from './fdr-geo.utils';

export interface IGeoMapRoundTrip {
  name: string;
  link: FdrLink;
  image?: FdrImage;
  productInfoB2B?: ProductInfoB2B;
}

export interface IGeoMapRoundTripDestination {
  id: string;
  coordinates?: FdrCoordinates;
  roundtrips: IGeoMapRoundTrip[];
}

export type TGeoMapRoundTripMap = Map<string, IGeoMapRoundTripDestination>;

const useGeoMapRoundTrips = (
  id: string
): {
  loading: boolean;
  roundTripMap: TGeoMapRoundTripMap;
  adventureTravels: IAdventureTravelRouteProduct[];
} => {
  const variables: FdrGeoMapProductsQueryVariables = {
    input: {
      publicId: id,
    },
    ...fdrGeoMapProductVars,
  };

  const { data, loading } = useFdrGeoMapProductsQuery({
    ssr: false,
    variables,
  });

  const roundTripMap: TGeoMapRoundTripMap = useMemo(() => {
    const initialMap = new Map<string, IGeoMapRoundTripDestination>();

    if (data?.fdrGeo && isFdrThGeoObject(data.fdrGeo)) {
      data.fdrGeo.roundtrips.productsPage
        ?.filter(isFdrRoundTripProduct)
        .forEach(r => {
          if (!initialMap.has(r.destination.id)) {
            initialMap.set(r.destination.id, {
              ...r.destination,
              roundtrips: [],
            });
          }

          initialMap.get(r.destination.id).roundtrips.push({
            name: r.name,
            image: r.hero,
            link: r.webMeta.meta.link,
            productInfoB2B: r.productInfoB2B,
          });
        });
    }

    return initialMap;
  }, [data]);

  const adventureTravels: IAdventureTravelRouteProduct[] = useMemo(() => {
    if (data?.fdrGeo && isFdrThGeoObject(data.fdrGeo)) {
      return (
        data.fdrGeo.adventureTravels.productsPage
          ?.filter(isFdrAdventureTravelProduct)
          .filter(
            at =>
              at.route?.length && at.headerImages?.[0] && at.webMeta.meta?.link
          )
          .map(at => ({
            ...at,
            routePoints: at.route || [],
            image: at.headerImages[0],
            link: at.webMeta.meta.link,
          })) || []
      );
    }

    return [];
  }, [data]);

  return { loading, roundTripMap, adventureTravels };
};

export default useGeoMapRoundTrips;
